import type { Session } from '@powerful/data';

import { getSupabaseClient } from '../supabase';
import { isNative } from '../platform';
import { accessTokenStatus } from './claims';

/**
 * Superfície única do app sobre o GoTrue.
 *
 * Nada fora de `lib/auth/` chama `supabase.auth` diretamente. A razão não é estética: as regras
 * que tornam este fluxo seguro — não distinguir email inexistente de palavra-passe errada, nunca
 * devolver sessão parcial, nunca escrever token em log — só valem se existir **um** sítio onde
 * elas são aplicadas. Espalhadas por telas, a primeira que esquecer uma delas abre o buraco.
 *
 * As funções **nunca lançam** para o chamador. Uma exceção que escapa deixa o contexto de sessão
 * preso em `isLoading: true`, e a tela fica num spinner permanente. O erro volta como valor e a UI
 * decide o que mostrar.
 */

export type AuthErrorCode =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'email_verification_expired'
  | 'session_expired'
  | 'rate_limited'
  | 'social_disabled'
  | 'age_restriction'
  | 'email_taken'
  | 'validation_error'
  | 'signup_failed'
  | 'account_unavailable'
  | 'network'
  | 'unknown';

export type AuthResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: AuthErrorCode; message: string };

/** Falha sem payload — o `data` é deliberadamente ausente, não nulo (ver teste de sessão parcial). */
export const failure = (code: AuthErrorCode, message: string): AuthResult<never> => ({
  ok: false,
  code,
  message,
});

const success = <T>(data: T): AuthResult<T> => ({ ok: true, data });

/** Forma mínima do erro do GoTrue de que dependemos. */
interface GotrueErrorLike {
  message?: string;
  code?: string;
  status?: number;
}

/**
 * Traduz o erro do GoTrue para o nosso vocabulário fechado.
 *
 * 🔴 `Invalid login credentials` cobre email inexistente **e** palavra-passe errada, e é assim que
 * tem de continuar: um código distinto para cada caso transformaria o login num oráculo que
 * confirma quem tem conta na plataforma (enumeração de contas).
 */
function classify(error: GotrueErrorLike): { code: AuthErrorCode; message: string } {
  const message = error.message ?? 'Erro de autenticação';
  const texto = message.toLowerCase();

  if (error.status === 429 || texto.includes('too many')) {
    return { code: 'rate_limited', message };
  }
  // O hook da T1.8 recusa `suspended`/`deleted` só DEPOIS de a palavra-passe ser validada, e com a
  // mesma mensagem para os dois: não há enumeração, e a UI também não os distingue.
  if (texto.includes('account unavailable')) {
    return { code: 'account_unavailable', message };
  }
  if (error.code === 'email_not_confirmed' || texto.includes('not confirmed')) {
    return { code: 'email_not_confirmed', message };
  }
  if (texto.includes('refresh token') || texto.includes('session')) {
    return { code: 'session_expired', message };
  }
  if (texto.includes('invalid login credentials') || texto.includes('invalid credentials')) {
    return { code: 'invalid_credentials', message };
  }
  // T1.19 (/code-review): recusa da política de senha é erro de quem preenche, não do servidor.
  if (error.code === 'weak_password' || error.code === 'same_password') {
    return { code: 'validation_error', message };
  }
  return { code: 'unknown', message };
}

/** URL única de retorno para emails de Auth, já presente na allowlist do GoTrue. */
function authCallbackUrl(flow?: 'signup' | 'recovery'): string {
  const suffix = flow ? `?flow=${flow}` : '';
  if (isNative()) return `pt.powerfulconnect.app://auth/callback${suffix}`;
  if (typeof window === 'undefined') return `/auth/callback${suffix}`;
  return `${window.location.origin}/auth/callback${suffix}`;
}

/**
 * Executa a chamada ao SDK isolando qualquer exceção.
 *
 * O `catch` existe para falha de transporte (offline, DNS, CORS), que o SDK propaga como throw em
 * vez de devolver em `error`. Sem isto, ficar sem rede a meio de um login seria um crash de app.
 */
async function attempt<T>(
  run: () => Promise<{ data: { session: Session | null }; error: GotrueErrorLike | null }>,
  extract: (session: Session) => T,
): Promise<AuthResult<T>> {
  let resultado;
  try {
    resultado = await run();
  } catch {
    // A mensagem do erro de transporte não entra no retorno: pode conter o URL completo do pedido.
    return failure('network', 'Sem ligação ao serviço de autenticação.');
  }

  if (resultado.error) {
    const { code, message } = classify(resultado.error);
    return failure(code, message);
  }
  if (!resultado.data.session) {
    // Sem erro e sem sessão: o signup com confirmação obrigatória cai aqui legitimamente, mas
    // quem chamou pediu uma sessão. Devolver `ok: true` sem dados daria sessão parcial.
    return failure('unknown', 'A resposta não trouxe sessão.');
  }
  return success(extract(resultado.data.session));
}

/**
 * Regista uma conta no GoTrue.
 *
 * 🔴 **Não é o caminho de signup do produto.** Use `signUpComGuarda` (`./signup.ts`), que passa pela
 * Edge Function `signup-guard`.
 *
 * Esta função cria a conta no GoTrue e mais nada: **sem perfil em `public.users` e sem a trava de
 * idade dos 16 anos**. Não é teoria — o gate remoto da T1.9 criou uma conta por aqui e observou-a
 * ficar com `user_status: pending_email_verification` e claims neutros, sem perfil nenhum.
 *
 * Fica exportada para diagnóstico e para fluxos que não criam utilizador de produto. Criar conta de
 * utilizador por aqui contorna o 🔴 C6 (PRD §10.10).
 */
export async function signUp(email: string, password: string): Promise<AuthResult<Session | null>> {
  try {
    const { data, error } = await getSupabaseClient().auth.signUp({ email, password });
    if (error) {
      const { code, message } = classify(error);
      return failure(code, message);
    }
    // Com confirmação de email obrigatória, `session` vem nula por desenho — e isso é sucesso.
    return success(data.session ?? null);
  } catch {
    return failure('network', 'Sem ligação ao serviço de autenticação.');
  }
}

/**
 * Dispara (ou reenvia) a confirmação de uma conta de email pendente.
 *
 * A chamada usa somente a credencial pública do cliente. A UI mantém a resposta genérica para não
 * transformar diferenças de estado da conta em enumeração; apenas o limite temporal é distinguido
 * para desabilitar o botão corretamente.
 */
export async function resendSignupConfirmation(email: string): Promise<AuthResult<null>> {
  try {
    const { error } = await getSupabaseClient().auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: authCallbackUrl('signup') },
    });
    if (error) {
      const { code, message } = classify(error);
      return failure(code, message);
    }
    return success(null);
  } catch {
    return failure('network', 'Sem ligação ao serviço de autenticação.');
  }
}

/** Promove, no servidor, a sessão que acabou de confirmar o email. */
export async function activateCurrentEmailVerification(): Promise<AuthResult<null>> {
  try {
    const { error } = await getSupabaseClient().functions.invoke('activate-email-verification', { body: {} });
    if (!error) return success(null);

    const response = (error as { context?: { status?: number } }).context;
    if (response?.status === 409) {
      return failure('email_verification_expired', 'A confirmação já não pode ativar esta conta.');
    }
    return failure('unknown', 'Não foi possível ativar a conta.');
  } catch {
    return failure('network', 'Sem ligação ao serviço de autenticação.');
  }
}

/** Solicita recuperação sem revelar se a identidade existe. */
export async function requestPasswordRecovery(email: string): Promise<AuthResult<null>> {
  try {
    const { error } = await getSupabaseClient().auth.resetPasswordForEmail(email, {
      redirectTo: authCallbackUrl('recovery'),
    });
    // O texto de sucesso é deliberadamente idêntico para erro de conta inexistente e sucesso.
    if (error?.status === 429) return failure('rate_limited', error.message ?? 'Demasiados pedidos.');
    if (error) return success(null);
    return success(null);
  } catch {
    return failure('network', 'Sem ligação ao serviço de autenticação.');
  }
}

/** A sessão de recuperação é exigida pelo GoTrue; esta função nunca transporta a senha para URL/log. */
export async function updatePassword(password: string): Promise<AuthResult<null>> {
  if (password.length < 8) {
    return failure('validation_error', 'A palavra-passe deve ter pelo menos 8 caracteres.');
  }
  try {
    const { error } = await getSupabaseClient().auth.updateUser({ password });
    if (error) {
      const { code, message } = classify(error);
      return failure(code, message);
    }
    return success(null);
  } catch {
    return failure('network', 'Sem ligação ao serviço de autenticação.');
  }
}

/**
 * Autentica com email e palavra-passe.
 *
 * 🔴 T1.17: uma conta em carência de exclusão (`deleted_pending`) recebe do hook uma sessão
 * **neutra**. O login dentro do prazo reativa a conta: chama `delete-account { reactivate }`,
 * renova a sessão e só devolve sucesso se os claims renovados já forem `active`. Qualquer falha
 * nesse caminho termina a sessão local — nunca se devolve uma sessão parcial.
 */
export async function signIn(email: string, password: string): Promise<AuthResult<Session>> {
  const result = await attempt(
    () => getSupabaseClient().auth.signInWithPassword({ email, password }),
    (session) => session,
  );
  if (!result.ok || accessTokenStatus(result.data) !== 'deleted_pending') return result;
  return reactivatePendingDeletion();
}

const ACCOUNT_UNAVAILABLE = 'Não foi possível entrar nesta conta.';

async function reactivatePendingDeletion(): Promise<AuthResult<Session>> {
  const abandon = async () => {
    await signOut();
    return failure('account_unavailable', ACCOUNT_UNAVAILABLE);
  };

  try {
    const { error } = await getSupabaseClient().functions.invoke('delete-account', {
      body: { action: 'reactivate' },
    });
    // 409 (carência vencida ou corrida com o job) e qualquer outra falha terminam igual: a conta
    // não é reativada e a sessão neutra não pode sobreviver no dispositivo.
    if (error) return abandon();
  } catch {
    return abandon();
  }

  // O token atual foi emitido com `deleted_pending`; só um refresh passa de novo pelo hook.
  const renewed = await refreshSession();
  if (!renewed.ok || accessTokenStatus(renewed.data) !== 'active') return abandon();
  return renewed;
}

/**
 * Termina a sessão neste dispositivo.
 *
 * Reporta sucesso mesmo se a chamada remota falhar: o SDK já limpou o armazenamento local, e uma
 * sessão presa no cliente é pior do que um token órfão no servidor — o utilizador carrega em
 * "sair", vê que continua dentro, e deixa de confiar no botão. O token expira em 15 min de qualquer
 * forma. Para revogação garantida existe `signOutEverywhere`.
 */
export async function signOut(): Promise<AuthResult<null>> {
  try {
    await getSupabaseClient().auth.signOut();
  } catch {
    // Intencionalmente engolido — ver acima.
  }
  return success(null);
}

/**
 * Termina a sessão em **todos** os dispositivos, revogando os refresh tokens (`RF-AUTH-04`).
 *
 * Ao contrário do `signOut` local, aqui a falha é reportada: quem pede "sair em todo o lado"
 * costuma suspeitar de acesso indevido, e dizer "feito" sem ter revogado nada seria mentir sobre
 * uma garantia de segurança.
 */
export async function signOutEverywhere(): Promise<AuthResult<null>> {
  try {
    const { error } = await getSupabaseClient().auth.signOut({ scope: 'global' });
    if (error) {
      const { code, message } = classify(error);
      return failure(code, message);
    }
    return success(null);
  } catch {
    return failure('network', 'Não foi possível terminar as sessões remotas.');
  }
}

/**
 * Renova a sessão a partir do refresh token.
 *
 * O `autoRefreshToken` do client trata do caso normal; esta função serve a renovação explícita e o
 * diagnóstico. Falha limpa a sessão — nunca deixa uma meio-válida.
 */
export async function refreshSession(): Promise<AuthResult<Session>> {
  return attempt(
    () => getSupabaseClient().auth.refreshSession(),
    (session) => session,
  );
}
